import React, { FC, useState, useMemo, useEffect } from 'react';
import { Button, Checkbox, Icon, Bubble, Radio } from 'tea-component';
import { RevenueFilterFiled, RevenueGroupItem } from '../../types';
import filterSvg from '@/pages/intelligence/assets/svg/pcConsoleBenchmark/filter.svg';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { dbiBtnLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import TipContext from '@/pages/home/components/TipContext';
import { useIntl, getLocale } from 'umi';
import RegionSelect from '../RegionSelect';

import { cloneDeep, isArray, reduce } from 'lodash-es';
import './index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';

interface RevenueSelectProps {
  value: any;
  regionOptions?: any[];
  configGroup: RevenueGroupItem[];
  onConfirm: (value: any) => void;
  onReset: () => void;
}

const GLOBAL_REGION = [
  {
    area_code: 'global',
    area_name_ch: '全球',
    area_name_en: 'Global',
  },
  {
    area_code: 'global_wo_china',
    area_name_ch: '全球（不包括中国大陆）',
    area_name_en: 'Global except China mainland',
  },
];

const RevenueSelector: FC<RevenueSelectProps> = (props) => {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const {
    value = {},
    onConfirm,
    onReset,
    regionOptions = [],
    configGroup = [],
  } = props;
  /** 是否展开收起 */
  const [isExpand, setIsExpand] = useState(true);

  const [revenueValue, setRevenueValue] = useState({});

  const handleGroupChange = (
    value: string[] | string,
    filed: RevenueFilterFiled,
  ) => {
    const copyValue = cloneDeep(revenueValue);

    copyValue[filed] = value;
    setRevenueValue(copyValue);
  };

  useEffect(() => {
    if (value) setRevenueValue(value);
  }, [value]);

  const isDisabled = useMemo(() => {
    // 每个筛选品类checkBox至少选一个
    const resultList = reduce(
      revenueValue,
      (result, value) => {
        if (Array.isArray(value)) {
          result.push(!!value.length);
        } else {
          result.push(true);
        }

        return result;
      },
      [] as boolean[],
    );
    return resultList.some((item) => !item);
  }, [revenueValue]);

  const collapseList = useMemo(() => {
    return reduce(
      revenueValue,
      (result, value, key) => {
        const targetRevenue = configGroup?.find((item) => item.value === key);
        if (targetRevenue) {
          switch (targetRevenue.type) {
            case 'radio':
              const targetOption = targetRevenue?.option?.find(
                (config) => config.value === value,
              );
              if (targetOption) {
                result.push({
                  label: formatMessage({ id: targetRevenue?.name || '' }),
                  value: formatMessage({ id: targetOption?.label || '' }),
                });
              }

              break;
            case 'checkbox':
              const nameList = value?.map((item) => {
                const targetOption = targetRevenue?.option?.find(
                  (config) => config.value === item,
                );
                if (targetOption) {
                  return formatMessage({ id: targetOption?.label || '' });
                }
                return '';
              });
              result.push({
                label: formatMessage({ id: targetRevenue?.name || '' }),
                value: nameList.join(', '),
              });
              break;
            case 'region':
              const allRegion = [...GLOBAL_REGION, ...regionOptions];
              const regionList = value?.map((item) => {
                const targetRegion = allRegion?.find(
                  (config) => config?.area_code === item,
                );

                if (targetRegion) {
                  return locale === 'zh-CN'
                    ? targetRegion?.area_name_ch
                    : targetRegion?.area_name_en;
                }
                return '';
              });
              result.push({
                label: formatMessage({ id: targetRevenue?.name || '' }),
                value: regionList?.join(','),
              });
              break;
            default:
              result.push({
                label: '',
                value: '',
              });
          }
        }
        return result;
      },
      [] as { label: string; value: string }[],
    );
  }, [revenueValue, locale, regionOptions]);

  const revenueGroupList = useMemo(() => {
    return configGroup?.map((item) => {
      return {
        ...item,
        option: item?.option?.map((config) => ({
          ...config,
          label: formatMessage({ id: config?.label }),
        })),
        name: formatMessage({ id: item?.name || '' }),
      };
    });
  }, [configGroup, locale]);

  const renderSelector = (item) => {
    switch (item.type) {
      case 'radio':
        return (
          <Radio.Group
            value={revenueValue[item.value]}
            onChange={(value) => handleGroupChange(value, item.value)}
          >
            {item.option.map((config, num) => (
              <Radio name={config.value} key={num}>
                <span>{config.label}</span>
                {config?.info && (
                  <TipContext
                    className="tip-icon"
                    AWidth={555}
                    placement="top"
                    tipText={formatMessage({ id: config?.info })}
                  />
                )}
              </Radio>
            ))}
          </Radio.Group>
        );
      case 'checkbox':
        return (
          <Checkbox.Group
            value={revenueValue[item.value]}
            onChange={(value) => handleGroupChange(value, item.value)}
          >
            {item.option.map((config, num) => (
              <Checkbox name={config.value} key={num}>
                <span>{config.label}</span>
                {config?.info && (
                  <TipContext
                    className="tip-icon"
                    AWidth={555}
                    placement="top"
                    tipText={formatMessage({ id: config?.info })}
                  />
                )}
              </Checkbox>
            ))}
          </Checkbox.Group>
        );
      case 'region':
        return (
          <RegionSelect
            value={revenueValue[item.value]}
            options={regionOptions || []}
            onChange={(value) => handleGroupChange(value, item.value)}
            disabledWoChina={item?.disabled || false}
          />
        );
      default:
        return '';
    }
  };

  return (
    <div className="benchmark-revenue-selector">
      {isExpand ? (
        <>
          {revenueGroupList.map((item, index) => (
            <div key={index} className="benchmark-revenue-selector-group">
              <div className="benchmark-revenue-selector-group-label">
                {item.name}
              </div>
              <div className="benchmark-revenue-selector-group-option">
                {renderSelector(item)}
              </div>
            </div>
          ))}
          <div className="benchmark-revenue-selector-footer">
            <div>
              <Button
                type="primary"
                className="mr20"
                disabled={isDisabled}
                onClick={() => {
                  onConfirm(revenueValue);
                }}
              >
                {formatMessage({ id: 'dbi_confirm' })}
              </Button>
              <Button
                onClick={() => {
                  setRevenueValue(value);
                  onReset();
                }}
              >
                {formatMessage({ id: 'dbi_reset' })}
              </Button>
            </div>
            <span className="show-selector" onClick={() => setIsExpand(false)}>
              <span>{locale === 'zh-CN' ? '关闭' : 'Close'} </span>
              <SvgIcon type="point-top" className="show-selector-icon" />
            </span>
          </div>
        </>
      ) : (
        <div className="benchmark-revenue-selector-shutting">
          <div className="collapse-content">
            <span className="collapse-content-filter">
              <img src={filterSvg} alt="" />
              <span>{formatMessage({ id: 'dbi_benchmark_my_filter' })}</span>
            </span>
            <div className="collapse-content-filed">
              {collapseList?.map((item, index) => (
                <span key={index} className="filed-item">
                  <span className="filed-item-label">{item?.label}</span>
                  <span className="filed-item-value">
                    <Bubble content={item?.value} placement="top-start" dark>
                      {item?.value}
                    </Bubble>
                  </span>
                </span>
              ))}
            </div>
          </div>
          <span className="show-selector" onClick={() => setIsExpand(true)}>
            <span>{locale === 'zh-CN' ? '展开' : 'Show'} </span>
            <SvgIcon type="point-down" className="show-selector-icon" />
          </span>
        </div>
      )}
    </div>
  );
};

RevenueSelector.displayName = 'RevenueSelect';

export default RevenueSelector;
